import {
  GIVE_BACK_OUTCOME_HELP,
  GIVE_BACK_OUTCOME_LABELS,
  GIVE_BACK_REFERENCE_MAX,
  formatMinor,
  giveBackAsks,
  giveBacksOf,
  normalizeGiveBackReference,
} from '@didto/domain';
import type { GiveBackOutcome, GiveBackView } from '@didto/domain';
import { Badge, Button, ErrorState, Field, Input, Pills, Sheet } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ReauthForm } from '@/app/ownerReview';
import type { AppError } from '@/lib/errors';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { hintOf } from '@/lib/orders';
import { fetchGiveBacks, giveBackKeys } from '@/lib/payments';
import { readSettled } from '@/lib/freshRead';
import type { useRun } from './posData';
import './shift.css';

/**
 * Money given back by a non-cash method (ADR-029). After a refund or a void that sent money back by
 * GCash or a bank transfer, the POS asks for the reference of the money sent, and a void is asked
 * what it was: money sent back, or a payment that never arrived. Both are optional at that moment and
 * can be given later from the order's money panel while the shift is open; the owner gives them when
 * checking. They are statements of their own: the refund or void, and its approval, are done.
 * Saving one needs the server: while it cannot be reached, nothing here can be saved (ADR-037).
 */

type Run = ReturnType<typeof useRun>;

interface Draft {
  outcome: GiveBackOutcome | null;
  reference: string;
}

/** The question after a money command, for the give-backs it made (by its reversal ids), or for
 * one give-back chosen from the money panel. Renders nothing when there is nothing to ask. */
export function GiveBackSheet({
  orderId,
  reversalIds,
  transferId,
  currency,
  run,
  onClose,
}: {
  orderId: string;
  reversalIds?: string[];
  transferId?: string;
  currency: string;
  run: Run;
  onClose: () => void;
}) {
  // the money panel shares this list: it may still be the one from before the refund or void that opened this sheet
  // (readSettled), so it is read again on opening and "nothing to ask" is decided on that read
  const q = useQuery({
    queryKey: giveBackKeys.order(orderId),
    queryFn: () => fetchGiveBacks(orderId),
    refetchOnMount: 'always',
  });
  const all = q.data ?? [];
  const list = (
    transferId
      ? all.filter((g) => g.transfer_id === transferId)
      : giveBacksOf(all, reversalIds ?? [])
  ).filter((g) => {
    const a = giveBackAsks(g);
    return transferId ? g.can_answer || g.can_reference : a.answer || a.reference;
  });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState(false);
  const online = useOnline();
  // the owner's signed-in account changing an answer, or answering money already checked, verifies first
  const [reauth, setReauth] = useState(false);
  const nothing = readSettled(q) && list.length === 0;
  useEffect(() => {
    if (nothing || q.isError) onClose();
  }, [nothing, q.isError, onClose]);
  if (!q.isSuccess || list.length === 0) return null;

  const draftOf = (g: GiveBackView): Draft =>
    drafts[g.transfer_id] ?? {
      outcome: g.outcome === 'sent_back' || g.outcome === 'never_arrived' ? g.outcome : null,
      reference: '',
    };
  const set = (g: GiveBackView, patch: Partial<Draft>) =>
    setDrafts((d) => ({ ...d, [g.transfer_id]: { ...draftOf(g), ...patch } }));
  const takesReference = (g: GiveBackView, d: Draft) =>
    g.can_reference && (g.kind === 'refund' || d.outcome !== 'never_arrived');

  // "already recorded" (a retry after a dropped connection) is saved; a fresh verification is asked for in the sheet
  const quiet = (e: AppError) => {
    if (e.code === 'reauth_required') {
      setReauth(true);
      return true;
    }
    return alreadyRecorded(e);
  };
  const save = async () => {
    setBusy(true);
    let ok = true;
    for (const g of list) {
      const d = draftOf(g);
      if (g.kind === 'void' && g.can_answer && d.outcome && d.outcome !== g.outcome) {
        const r = await run(
          'cash.give_back_outcome',
          { reversal_id: g.transfer_id, outcome: d.outcome },
          { handle: quiet },
        );
        if (!r.ok && !alreadyRecorded(r.error)) {
          ok = false;
          if (r.error.code === 'reauth_required') break;
          continue;
        }
      }
      const ref = normalizeGiveBackReference(d.reference);
      if (ref && takesReference(g, d)) {
        const r = await run(
          'cash.give_back_reference',
          { reversal_id: g.transfer_id, reference: ref },
          { handle: quiet },
        );
        if (!r.ok && !alreadyRecorded(r.error)) {
          ok = false;
          if (r.error.code === 'reauth_required') break;
        }
      }
    }
    setBusy(false);
    if (ok) onClose();
  };
  const ready = list.some((g) => {
    const d = draftOf(g);
    return (
      (g.kind === 'void' && g.can_answer && d.outcome !== null && d.outcome !== g.outcome) ||
      (takesReference(g, d) && normalizeGiveBackReference(d.reference) !== null)
    );
  });

  if (reauth)
    return (
      <Sheet open onClose={() => setReauth(false)} title="Confirm it is you">
        <ReauthForm
          reason="action"
          note="Changing an answer, or answering money already checked, asks for a fresh verification."
          onVerified={() => {
            setReauth(false);
            void save();
          }}
          onCancel={() => setReauth(false)}
        />
      </Sheet>
    );

  return (
    <Sheet open onClose={onClose} title="Money given back">
      <div className="d-stack" data-testid="give-back-sheet">
        {list.map((g) => {
          const d = draftOf(g);
          return (
            <div key={g.transfer_id} className="d-stack money-transfer" data-testid="give-back-row">
              <div className="d-spread">
                <span className="money-subject__title">
                  {g.kind === 'void' ? 'Void' : 'Refund'} · {g.method}
                </span>
                <strong className="d-mono money-subject__title">
                  {formatMinor(g.amount_minor, currency)}
                </strong>
              </div>
              {g.kind === 'void' && g.can_answer && (
                <>
                  <Pills
                    ariaLabel="What happened to the money"
                    value={d.outcome ?? ''}
                    onChange={(v) => set(g, { outcome: v as GiveBackOutcome })}
                    options={(['sent_back', 'never_arrived'] as const).map((o) => ({
                      value: o,
                      label: GIVE_BACK_OUTCOME_LABELS[o],
                    }))}
                  />
                  {d.outcome && <p className="d-help">{GIVE_BACK_OUTCOME_HELP[d.outcome]}</p>}
                </>
              )}
              {takesReference(g, d) && (
                <Field
                  label={`${g.method} reference of the money sent`}
                  help={
                    g.reference_last4
                      ? `Recorded: ending ${g.reference_last4}. Type it again to change it.`
                      : 'Optional now; it can be added later from the order.'
                  }
                >
                  {(id) => (
                    <Input
                      id={id}
                      value={d.reference}
                      maxLength={GIVE_BACK_REFERENCE_MAX}
                      onChange={(e) => set(g, { reference: e.target.value })}
                      data-testid="give-back-reference"
                    />
                  )}
                </Field>
              )}
            </div>
          );
        })}
        <div className="d-sheet__actions">
          <Button variant="quiet" onClick={onClose} data-testid="give-back-later">
            Later
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={!online || !ready}
            onClick={() => void save()}
            data-testid="give-back-save"
          >
            Save
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/** The order's money given back by a non-cash method, under its money panel: the reference's last
 * four characters, a void's answer, and what may still be given here. */
export function GiveBacksPanel({
  orderId,
  currency,
  run,
}: {
  orderId: string;
  currency: string;
  run: Run;
}) {
  const q = useQuery({
    queryKey: giveBackKeys.order(orderId),
    queryFn: () => fetchGiveBacks(orderId),
  });
  const [open, setOpen] = useState<string | null>(null);
  const online = useOnline();
  const list = q.data ?? [];
  if (list.length === 0)
    // a refresh that fails after a read keeps that read (nothing given back) instead of an error
    return q.isError && q.data === undefined ? (
      <ErrorState
        title="Could not load the money given back"
        message={errorText(q.error)}
        onRetry={() => void q.refetch()}
      />
    ) : null;
  return (
    <div className="d-stack d-stack--tight" data-testid="give-backs">
      <div className="d-eyebrow">Money given back</div>
      <div className="d-list">
        {list.map((g) => (
          <div
            className="d-cartline"
            key={g.transfer_id}
            data-testid="give-back-line"
            data-outcome={g.outcome ?? ''}
          >
            <div className="d-cartline__main">
              <div className="d-row__title">
                {g.kind === 'void' ? 'Void' : 'Refund'} · {g.method}
              </div>
              <div className="d-row__meta">
                {g.cancelled
                  ? 'cancelled'
                  : g.outcome === 'never_arrived' || g.outcome === 'withdrawn'
                    ? 'nothing was sent'
                    : g.reference_last4
                      ? `reference ending ${g.reference_last4}`
                      : 'no reference yet'}
              </div>
            </div>
            <div className="d-cartline__side">
              <strong className="d-mono">{formatMinor(g.amount_minor, currency)}</strong>
              {g.outcome ? (
                <Badge tone={g.outcome === 'sent_back' ? 'neutral' : 'warning'}>
                  {GIVE_BACK_OUTCOME_LABELS[g.outcome]}
                </Badge>
              ) : g.kind === 'void' ? (
                <Badge tone="warning">Not answered</Badge>
              ) : null}
              {(g.can_answer || g.can_reference) && (
                <Button
                  size="sm"
                  variant="quiet"
                  disabled={!online}
                  onClick={() => setOpen(g.transfer_id)}
                  data-testid="give-back-edit"
                >
                  {g.kind === 'void' && g.outcome === null ? 'Answer' : 'Reference'}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {open && (
        <GiveBackSheet
          orderId={orderId}
          transferId={open}
          currency={currency}
          run={run}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** The statement is already what was sent: a retry after a dropped connection, nothing to do. */
function alreadyRecorded(e: AppError): boolean {
  return hintOf(e).give_back === 'no_change';
}

/** The reversal ids a money command's result names (a void names one, a refund or an order void
 * several). */
export function reversalIdsOf(result: Record<string, unknown> | null | undefined): string[] {
  if (!result) return [];
  const many = result.reversal_ids;
  if (Array.isArray(many)) return many.filter((x): x is string => typeof x === 'string');
  return typeof result.reversal_id === 'string' ? [result.reversal_id] : [];
}
